import { Form, Formik } from 'formik';
import InputText from '../../../components/input-text/inputText';
import ModalComponent from '../../../components/modal/modal';
import { getFilteredPermissionData } from '../../../utils';
import { ROLES_VALIDATION } from '../../../utils/validations/validations';
import {
  DEFAULT_TENANT_SCOPE,
  resolveTenantScope,
  type TenantScope,
} from '../../../constants/tenantScope';
import InstanceScopeBanner from '../InstanceScopeBanner';
import { type ModalStatusType } from '../../../types/common';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import CustomTextArea from '../../../components/text-area/textArea';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import { SORTED_FIELD_PERMISSION, SORTED_MODULE_PERMISSION } from '../../../constants/common';
import { handleFilterPermissionList, handleSortPermissionNames } from '../../../utils/global';
import { usePostSystemRoles, useWealthlaneCustomerApi } from '../../../hooks';
import ModalBody from '../../../components/modal/modalBodyComponent';
import _ from 'lodash';
import useGetFeaturePermissionSet from '../../../hooks/get/useGetFeaturePermissionSet';
import { convertPermissions } from '../../../utils/permission/permissionAdapter';
import { useState } from 'react';
import {
  PermissionMatrixForm,
  useFilteredPermissionMatrix,
  PERMISSION_MODAL_TOOLTIP,
  getPermissionModalTitle,
  type PermissionChangeLog,
} from '../shared/permission-matrix';

interface RoleModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus: (value: ModalStatusType) => void;
  setRolesInitialData: () => void;
  rolesInitialData: { data: any; id: string };
  loading: boolean;
  scope: TenantScope;
}

const RoleModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  rolesInitialData,
  setModalStatus,
  setRolesInitialData,
  loading,
  scope,
}: RoleModalType): JSX.Element => {
  const tenantScope = resolveTenantScope(scope);
  const isInstanceScope = tenantScope !== DEFAULT_TENANT_SCOPE;
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: newPermissions } = useGetFeaturePermissionSet(handleRequest);

  const userAuthList = useSelector(selectAuthRoutesList);
  const allFieldValues = userAuthList.localization?.values?.Wealthlane;

  const { nonFieldLevelPermissionsGroups, fieldLevelPermissionsGroups } = getFilteredPermissionData(
    convertPermissions(newPermissions ?? []),
    'CustomerService.Customer.',
    allFieldValues
  );
  const [manualSearch, setManualSearch] = useState('');

  const filterNonFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(nonFieldLevelPermissionsGroups),
    SORTED_MODULE_PERMISSION
  );
  const filterFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(fieldLevelPermissionsGroups),
    SORTED_FIELD_PERMISSION
  );

  const { filteredData, filteredFieldLevelData } = useFilteredPermissionMatrix({
    filterNonFieldLevelPermission,
    filterFieldLevelPermission,
    manualSearch,
    allFieldValues,
  });

  const permissionChangeLogs = rolesInitialData.data?.permissionChangeLogs as
    | PermissionChangeLog[]
    | null
    | undefined;

  const { mutate, isLoading } = usePostSystemRoles(handleRequest, closeModal, rolesInitialData.id);

  const handleSubmit = (values: any): void => {
    delete values.permissions['CustomerService-Customer'];
    delete values.fieldLevelPermissions['CustomerService-Customer-FieldLevel'];
    const payload = {
      ...values,
      scope: tenantScope,
      permissions: Object.keys(values?.permissions)
        .filter((x) => values.permissions[x] !== false)
        .map((item) => item.replaceAll('-', '.')),
      fieldLevelPermissions: Object.keys(values?.fieldLevelPermissions)
        .filter((x) => values.fieldLevelPermissions[x] !== false)
        .map((item) => item.replaceAll('-', '.')),
    };
    mutate(payload);
  };

  const handleCheckedAll = (
    key: string,
    setFieldValue: any,
    dataList: any,
    wrapperName: string,
    isCheckedAll: boolean
  ): void => {
    const allFields = handleFilterPermissionList(dataList)[key];
    const filterAllDisabledData = allFields.filter(
      (item: any) => !(item.isDisabled as boolean) && item
    );

    if (isCheckedAll) {
      allFields.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, false)
      );
    } else {
      filterAllDisabledData.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, true)
      );
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={rolesInitialData.data}
      validationSchema={ROLES_VALIDATION}
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {({ touched, handleChange, setFieldValue, errors, values }) => {
        return (
          <ModalComponent
            id='roleModalContainer'
            show={isModalOpen}
            closeModal={() => {
              closeModal();
              setRolesInitialData();
            }}
            loading={loading}
            size={modalStatus !== 'Add' ? '3xl' : 'lg'}
            valueEdited={_.isEqual(values, rolesInitialData?.data)}
            title={getPermissionModalTitle(modalStatus, 'role', rolesInitialData.data.roleName)}
            showToolTip={true}
            tooltipText={PERMISSION_MODAL_TOOLTIP}
            children={
              <Form>
                <ModalBody>
                  {isInstanceScope && (
                    <InstanceScopeBanner
                      semanticId='instanceRoleModalScopeBanner'
                      className='mb-[14px]'
                    />
                  )}
                  <div className='grid sm:grid-cols-4 form-gap'>
                    <div className='sm:col-span-3'>
                      <InputText
                        type='text'
                        label='Role Name'
                        name='roleName'
                        id='roleName'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        required
                      />
                    </div>
                    <div className='sm:col-span-3'>
                      <CustomTextArea
                        label='Description'
                        name='description'
                        id='description'
                        onChange={handleChange}
                        errors={errors}
                        inputType={modalStatus}
                        disabled={modalStatus === 'View'}
                        touched={touched}
                        rows='5'
                      />
                    </div>
                  </div>

                  <PermissionMatrixForm
                    variant='single'
                    modalStatus={modalStatus}
                    values={values}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    allFieldValues={allFieldValues}
                    filteredData={filteredData}
                    filteredFieldLevelData={filteredFieldLevelData}
                    nonFieldLevelPermissionsGroups={nonFieldLevelPermissionsGroups}
                    fieldLevelPermissionsGroups={fieldLevelPermissionsGroups}
                    filterNonFieldLevelPermission={filterNonFieldLevelPermission}
                    filterFieldLevelPermission={filterFieldLevelPermission}
                    manualSearch={manualSearch}
                    onSearchChange={setManualSearch}
                    searchInputId='searchRoleInput'
                    handleCheckedAll={handleCheckedAll}
                    permissionChangeLogs={permissionChangeLogs}
                  />
                </ModalBody>
                <ModalFooterComponent
                  modalStatus={modalStatus}
                  onCloseModal={() => {
                    closeModal();
                    setRolesInitialData();
                  }}
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  loading={isLoading}
                  valueChanged={_.isEqual(values, rolesInitialData?.data)}
                  isEditShow={(rolesInitialData.data.isEditable as boolean) || false}
                  id='modalFooter'
                  urlName='roles'
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default RoleModal;
